import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    BuildingOffice2Icon,
    CalendarDaysIcon,
    ChatBubbleLeftRightIcon,
    ClipboardDocumentCheckIcon,
    CogIcon,
    CreditCardIcon,
    CubeTransparentIcon,
    EnvelopeIcon,
    FolderIcon,
    HomeIcon,
    HomeModernIcon,
    InboxStackIcon,
    MegaphoneIcon,
    StarIcon,
    TicketIcon,
    TrophyIcon,
} from '@heroicons/react/24/outline';
import { useStateContext } from '../../../contexts/ContextProvider';
import { usePermissions } from '../../../contexts/PermissionContext';
import { CardexIcon, FlowIcon, ProductsIcon } from '../../../icons/globalIcons';
import { useUnreadConversationsCount } from '../../../pages/messaging/api/useUnreadConversationsCount';
import { useChangelogUnreadCount } from '../../../pages/changelog/api/useChangelog';
import { NavItem, NavSection } from './types';

/**
 * Menu client : ce que voit un hôtelier, et ce que voit un membre de l'équipe
 * hors mode super admin (démo).
 *
 * @param enabled faux en mode super admin : les compteurs des badges ne sont
 *                plus interrogés puisque ce menu n'est pas affiché.
 */
export function useClientMenuSections(enabled: boolean) {
    const { t } = useTranslation('navigation');
    const { user, isUserLoading, currentFacility } = useStateContext();
    const { hasPermission } = usePermissions();

    const canViewMessaging = hasPermission('messaging.view');
    const { data: unreadData } = useUnreadConversationsCount(currentFacility?.id, enabled && canViewMessaging);
    const unreadConversationsCount = unreadData?.unread_conversations_count ?? 0;

    const canViewChangelog = hasPermission('changelog.view');
    const { data: changelogUnreadData } = useChangelogUnreadCount(enabled && canViewChangelog);
    const changelogUnreadCount = canViewChangelog ? (changelogUnreadData?.unread_count ?? 0) : 0;

    return useMemo(() => {
        if (isUserLoading || user.id === 0) {
            return { sections: [] as NavSection[], modules: null, changelog: null };
        }

        const canView = (permission: string) => !permission || hasPermission(permission);

        const item = (link: string, icon: JSX.Element, text: string, extra: Partial<NavItem> = {}): NavItem => ({
            key: link, link, icon, text, ...extra,
        });
        const compact = (items: (NavItem | false | null | undefined)[]) => items.filter(Boolean) as NavItem[];

        // « Ventes » : catalogue (products.view) ou, à défaut, le seul module Demandes de réservation.
        const canViewProducts = canView('products.view');
        const canViewBookingRequests = !!currentFacility?.admin_has_booking_requests && canView('booking_requests.view');

        const sections: NavSection[] = [
            {
                key: 'dashboard',
                items: compact([canView('dashboard.view') && item('/dashboard', <HomeIcon className="w-6 h-6"/>, t('menu.dashboard'))]),
            },
            {
                key: 'clients',
                label: t('sections.clients'),
                items: compact([
                    canView('customers.view') && item('/cardex', <CardexIcon className="w-6 h-6"/>, t('menu.cardex'), {
                        isActive: (path) => path.startsWith('/cardex') || path.startsWith('/newsletter-widgets'),
                    }),
                    currentFacility?.admin_has_companies && canView('companies.view') && item('/companies', <BuildingOffice2Icon className="w-6 h-6"/>, t('menu.companies')),
                    currentFacility?.admin_has_events && canView('events.view') && item('/events', <TicketIcon className="w-6 h-6"/>, t('menu.events')),
                    canView('customers.view') && item('/reservations', <CalendarDaysIcon className="w-6 h-6"/>, t('menu.reservations')),
                    canView('segments.view') && item('/segments', <InboxStackIcon className="w-6 h-6"/>, t('menu.segments')),
                    currentFacility?.admin_has_loyalty && canView('loyalty.view') && item('/loyalty', <TrophyIcon className="w-6 h-6"/>, t('menu.loyalty')),
                    currentFacility?.admin_has_housekeeping && canView('housekeeping.view') && item('/housekeeping', <HomeModernIcon className="w-6 h-6"/>, t('menu.housekeeping')),
                ]),
            },
            {
                key: 'communication',
                label: t('sections.communication'),
                items: compact([
                    canView('messaging.view') && item('/messaging', <ChatBubbleLeftRightIcon className="w-6 h-6"/>, t('menu.messaging'), { badgeCount: unreadConversationsCount }),
                    canView('email_templates.view') && item('/email-templates', <EnvelopeIcon className="w-6 h-6"/>, t('menu.email_templates')),
                    canView('flows.view') && item('/flows', <FlowIcon className="w-6 h-6"/>, t('menu.flows')),
                ]),
            },
            {
                key: 'feedback',
                label: t('sections.feedback'),
                items: compact([
                    canView('surveys.view') && item('/surveys', <ClipboardDocumentCheckIcon className="w-6 h-6"/>, t('menu.surveys')),
                    currentFacility?.admin_has_reviews && canView('reviews.view') && item('/reviews', <StarIcon className="w-6 h-6"/>, t('menu.reviews')),
                ]),
            },
            {
                key: 'upsell',
                label: t('sections.upsell'),
                items: compact([
                    (canViewProducts || canViewBookingRequests) && item(
                        canViewProducts ? '/products' : '/products/booking-requests',
                        <ProductsIcon className="w-6 h-6"/>,
                        t('menu.products'),
                        { isActive: (path) => path.startsWith('/products') },
                    ),
                    currentFacility?.admin_has_payment && canView('payments.view') && item('/payments', <CreditCardIcon className="w-6 h-6"/>, t('menu.payments')),
                ]),
            },
            {
                key: 'general',
                label: t('sections.general'),
                items: compact([
                    canView('reports.view') && item('/files', <FolderIcon className="w-6 h-6"/>, t('menu.files')),
                    canView('settings.view') && item('/settings', <CogIcon className="w-6 h-6"/>, t('menu.settings')),
                ]),
            },
        ];

        return {
            sections,
            // Entrées isolées du bas du menu : Modules (admins) et Nouveautés (panneau).
            modules: user.is_admin
                ? { link: '/modules', icon: <CubeTransparentIcon className="w-6 h-6"/>, text: t('menu.modules') }
                : null,
            changelog: canView('changelog.view')
                ? { icon: <MegaphoneIcon className="w-6 h-6"/>, text: t('menu.changelog'), badgeCount: changelogUnreadCount }
                : null,
        };
    }, [
        t, hasPermission, isUserLoading, user.id, user.is_admin,
        currentFacility?.id, currentFacility?.admin_has_loyalty, currentFacility?.admin_has_reviews,
        currentFacility?.admin_has_payment, currentFacility?.admin_has_housekeeping,
        currentFacility?.admin_has_companies, currentFacility?.admin_has_events, currentFacility?.admin_has_booking_requests,
        unreadConversationsCount, changelogUnreadCount,
    ]);
}
